/**
 * Dubai (UAE) Hospitality Tax, Fee & Government Clearance Utility
 *
 * Implements verified UAE/Dubai hospitality tax and fee calculation rules
 * in accordance with UAE Federal Decree-Law No. (8) of 2017 (VAT),
 * Dubai Executive Council & DTCM/DET Regulations (Tourism Dirham Fee),
 * Dubai Municipality Fees, and standard UAE hospitality service charges.
 */

// Statutory Default Rates (Dubai, UAE)
export const DUBAI_DEFAULT_RATES = {
  vatPct: 5.0,               // 5% UAE Federal VAT
  municipalityFeePct: 7.0,   // 7% Dubai Municipality Fee
  serviceChargePct: 10.0,    // 10% Hotel Service Charge
  tourismDirhamByRating: {
    5: 20, // 5-Star Hotel / Deluxe Hotel Apartments: AED 20 per bedroom per night
    4: 15, // 4-Star Hotel / Superior Hotel Apartments: AED 15 per bedroom per night
    3: 10, // 3-Star Hotel / Standard Hotel Apartments: AED 10 per bedroom per night
    2: 10, // 2-Star Hotel / Standard Hotel Apartments: AED 10 per bedroom per night
    1: 7   // 1-Star Hotel / Budget / Guest House: AED 7 per bedroom per night
  },
  defaultTourismDirham: 15,
  maxTourismDirhamNights: 30 // Capped at 30 consecutive nights per stay
};

/**
 * Checks if a property is located in the UAE or configured with UAE currency.
 */
export const isUaeProperty = (property = {}) => {
  if (!property) return false;
  const country = (property.country || '').trim().toLowerCase();
  const currency = (property.currency || '').trim().toUpperCase();
  const city = (property.city || '').trim().toLowerCase();
  const state = (property.state || '').trim().toLowerCase();
  const address = (property.address || '').trim().toLowerCase();

  return (
    country.includes('uae') ||
    country.includes('united arab emirates') ||
    country.includes('emirates') ||
    currency === 'AED' ||
    city.includes('dubai') ||
    city.includes('abu dhabi') ||
    city.includes('sharjah') ||
    city.includes('ajman') ||
    city.includes('ras al khaimah') ||
    city.includes('fujairah') ||
    city.includes('umm al quwain') ||
    state.includes('dubai') ||
    address.includes('dubai')
  );
};

/**
 * Checks if a property is located specifically in the Emirate of Dubai.
 */
export const isDubaiProperty = (property = {}) => {
  if (!property) return false;
  const city = (property.city || '').trim().toLowerCase();
  const state = (property.state || '').trim().toLowerCase();
  const address = (property.address || '').trim().toLowerCase();
  const location = (property.location || '').trim().toLowerCase();

  return (
    city.includes('dubai') ||
    city.includes('dxb') ||
    state.includes('dubai') ||
    address.includes('dubai') ||
    location.includes('dubai')
  );
};

/**
 * Extracts UAE statutory tax and fee rates from the active tax slabs array
 * returned by the PMS tax slabs API (/settings/tax-slabs?noPagination=true).
 *
 * @param {Array|Object} taxes - Tax slabs array or API response wrapper
 * @returns {Object} Extracted custom rates { vatPct, municipalityFeePct, serviceChargePct, tourismDirhamRate, ... }
 */
export const extractUaeCustomRates = (taxes = []) => {
  const customRates = {};
  const taxList = Array.isArray(taxes) ? taxes : (taxes?.data || taxes?.taxes || []);
  for (const t of taxList) {
    if (!t || t.status === 'Inactive') continue;
    const s = String(t.serviceType || t.type || t.applicableTo || '').toLowerCase();
    const n = String(t.name || '').toLowerCase();
    const rate = Number(
      t.rate !== undefined && t.rate !== null && t.rate !== ''
        ? t.rate
        : (t.gstPct !== undefined && t.gstPct !== null && t.gstPct !== ''
            ? t.gstPct
            : (Number(t.cgst || t.cgstPct || 0) + Number(t.sgst || t.sgstPct || 0)))
    );

    if (s === 'vat' || n.includes('vat')) {
      customRates.vatPct = rate;
      customRates.vatId = t.id || t._id;
      customRates.vatName = t.name;
    } else if (s === 'municipality' || n.includes('municipality')) {
      customRates.municipalityFeePct = rate;
      customRates.municipalityId = t.id || t._id;
      customRates.municipalityName = t.name;
    } else if (s === 'service_charge' || n.includes('service charge')) {
      customRates.serviceChargePct = rate;
      customRates.serviceChargeId = t.id || t._id;
      customRates.serviceChargeName = t.name;
    } else if (s === 'tourism_dirham' || n.includes('tourism dirham') || t.rateType === 'flat_per_night') {
      customRates.tourismDirhamRate = rate;
      customRates.tourismDirhamId = t.id || t._id;
      customRates.tourismDirhamName = t.name;
    }
  }
  return customRates;
};

/**
 * Resolves statutory Tourism Dirham rate per bedroom per night
 * based on hotel classification (star rating and hotel type).
 */
export const getTourismDirhamRate = (rating, hotelType) => {
  const typeStr = (hotelType || '').toLowerCase();
  if (typeStr.includes('deluxe') || typeStr.includes('5 star') || typeStr.includes('5-star')) return 20;
  if (typeStr.includes('superior') || typeStr.includes('4 star') || typeStr.includes('4-star')) return 15;
  if (
    typeStr.includes('3 star') ||
    typeStr.includes('3-star') ||
    typeStr.includes('2 star') ||
    typeStr.includes('2-star') ||
    (typeStr.includes('standard') && (typeStr.includes('apartment') || typeStr.includes('hotel')))
  ) return 10;
  if (typeStr.includes('1 star') || typeStr.includes('1-star') || typeStr.includes('budget') || typeStr.includes('guest house')) return 7;

  // Extract numeric rating if passed as number or string (e.g. 5, "5", "5-Star", "4.5")
  const match = String(rating || '').match(/(\d+(\.\d+)?)/);
  const r = match ? parseFloat(match[1]) : 0;
  if (r >= 5) return 20;
  if (r >= 4) return 15;
  if (r >= 2) return 10;
  if (r >= 1) return 7;

  return 15; // Standard fallback (4-Star hotel benchmark)
};

/**
 * Returns the descriptive tier label for Dubai Tourism Dirham based on hotel rating or type.
 *
 * @param {number|string} rating - Hotel star rating (e.g. 5, 4, "5", "5-Star")
 * @param {string} [hotelType] - Property classification
 * @returns {string} Tier label e.g. "5-Star tier", "4-Star tier"
 */
export const getTourismDirhamTierLabel = (rating, hotelType = '') => {
  const match = String(rating || '').match(/(\d+(\.\d+)?)/);
  const r = match ? parseFloat(match[1]) : 0;
  if (r >= 5) return '5-Star tier';
  if (r >= 4) return '4-Star tier';
  if (r >= 3) return '3-Star tier';
  if (r >= 2) return '2-Star tier';
  if (r >= 1) return '1-Star tier';

  const typeStr = (hotelType || '').toLowerCase();
  if (typeStr.includes('deluxe') || typeStr.includes('5 star') || typeStr.includes('5-star')) return '5-Star tier';
  if (typeStr.includes('superior') || typeStr.includes('4 star') || typeStr.includes('4-star')) return '4-Star tier';
  if (typeStr.includes('3 star') || typeStr.includes('3-star')) return '3-Star tier';
  if (typeStr.includes('2 star') || typeStr.includes('2-star')) return '2-Star tier';
  if (typeStr.includes('1 star') || typeStr.includes('1-star') || typeStr.includes('budget') || typeStr.includes('guest house')) return '1-Star tier';

  return '4-Star tier';
};

/**
 * Calculates Dubai hospitality taxes and fees according to official business rules.
 *
 * Tax Order & Base:
 * 1. Net Room Base = Room Tariff - Discount
 * 2. Service Charge = Net Room Base * serviceChargeRate (default 10%)
 * 3. Municipality Fee = Net Room Base * municipalityFeeRate (default 7%)
 * 4. VAT Base = Net Room Base + Service Charge + Municipality Fee
 * 5. VAT = VAT Base * vatRate (5%)
 * 6. Tourism Dirham = Dirham Rate * Billable Nights (capped at 30) * Bedrooms Count
 *
 * @param {Object} options
 * @param {number} options.netBaseAmount - Room base tariff after promo/discount
 * @param {number} options.nights - Total stay nights (default 1)
 * @param {number} [options.priorNights] - Prior consecutive nights (for extensions)
 * @param {number} [options.bedroomsCount] - Bedrooms count (default 1)
 * @param {number|string} [options.rating] - Star rating of property
 * @param {string} [options.hotelType] - Property classification
 * @param {Object} [options.customRates] - Custom rates from backend API if configured
 * @param {Array}  [options.taxes] - Raw or active tax slabs fetched from backend API
 * @param {Array}  [options.activeTaxes] - Active tax slabs fetched from backend API
 * @param {Array}  [options.taxSlabs] - Tax slabs fetched from backend API
 * @returns {Object} Calculated tax breakdown and total
 */
export const calculateDubaiHospitalityTax = (options = {}) => {
  const {
    netBaseAmount,
    roomTariff,
    baseAmount,
    nights = 1,
    priorNights = 0,
    bedroomsCount,
    bedrooms,
    rating,
    starRating,
    hotelType = '',
    customRates: customRatesProp = null,
    taxes = null,
    activeTaxes = null,
    taxSlabs = null
  } = options;

  const rawBase = netBaseAmount !== undefined ? netBaseAmount : (roomTariff !== undefined ? roomTariff : baseAmount);
  const base = Math.max(0, Number(rawBase) || 0);
  const stayNights = Math.max(1, Number(nights) || 1);
  const rawBedrooms = bedroomsCount !== undefined ? bedroomsCount : (bedrooms !== undefined ? bedrooms : 1);
  const resolvedBedrooms = Math.max(1, Number(rawBedrooms) || 1);
  const resolvedRating = rating !== undefined ? rating : (starRating !== undefined ? starRating : 4);

  // Resolve custom rates from direct prop or by extracting from tax slabs
  const customRates = customRatesProp || (taxes || activeTaxes || taxSlabs ? extractUaeCustomRates(taxes || activeTaxes || taxSlabs) : null);

  // 1. Resolve fee percentages (custom rates prioritize backend config)
  const vatPct = customRates?.vatPct !== undefined ? Number(customRates.vatPct) : DUBAI_DEFAULT_RATES.vatPct;
  const municipalityFeePct = customRates?.municipalityFeePct !== undefined ? Number(customRates.municipalityFeePct) : DUBAI_DEFAULT_RATES.municipalityFeePct;
  const serviceChargePct = customRates?.serviceChargePct !== undefined ? Number(customRates.serviceChargePct) : DUBAI_DEFAULT_RATES.serviceChargePct;

  // 2. Resolve Tourism Dirham rate per bedroom/night
  const tdRate = customRates?.tourismDirhamRate !== undefined
    ? Number(customRates.tourismDirhamRate)
    : getTourismDirhamRate(resolvedRating, hotelType);

  // 3. Tourism Dirham 30-consecutive-night rule
  const prior = Math.max(0, Number(priorNights) || 0);
  const maxNights = DUBAI_DEFAULT_RATES.maxTourismDirhamNights;
  const chargeablePrior = Math.min(prior, maxNights);
  const chargeableTotal = Math.min(prior + stayNights, maxNights);
  const billableTdNights = Math.max(0, chargeableTotal - chargeablePrior);
  const tourismDirhamAmount = Number((tdRate * billableTdNights * resolvedBedrooms).toFixed(2));

  // 4. Calculate Service Charge & Municipality Fee on room base
  const serviceChargeAmount = Number(((base * serviceChargePct) / 100).toFixed(2));
  const municipalityFeeAmount = Number(((base * municipalityFeePct) / 100).toFixed(2));

  // 5. VAT Base includes base + service charge + municipality fee (statutory UAE FTA treatment)
  const vatBase = base + serviceChargeAmount + municipalityFeeAmount;
  const vatAmount = Number(((vatBase * vatPct) / 100).toFixed(2));

  // 6. Total Taxes & Final Total
  const totalTaxAmount = Number((serviceChargeAmount + municipalityFeeAmount + vatAmount + tourismDirhamAmount).toFixed(2));
  const finalTotal = Number((base + totalTaxAmount).toFixed(2));

  return {
    netBase: base,
    base,
    serviceChargePct,
    serviceChargeAmount,
    serviceCharge: serviceChargeAmount,
    municipalityFeePct,
    municipalityFeeAmount,
    municipalityFee: municipalityFeeAmount,
    vatPct,
    vatBase,
    vatAmount,
    vat: vatAmount,
    tourismDirhamRate: tdRate,
    tourismDirhamNights: billableTdNights,
    tourismDirhamAmount,
    tourismDirham: tourismDirhamAmount,
    totalTaxAmount,
    totalTax: totalTaxAmount,
    finalTotal,
    total: finalTotal,
    totalAmount: finalTotal,
    isUaeTax: true,
    isDubaiTax: true
  };
};

/**
 * Calculates UAE / Dubai taxes for incidental and folio line-item charges
 * (e.g., Extra Bed, Food & Beverage, Spa & Wellness, Laundry, Parking, Cancellation).
 *
 * UAE FTA Golden Rule:
 * 5% VAT is NOT calculated on the base price alone.
 * It is calculated on the Subtotal (Base Price + Service Charge + Municipality Fee).
 *
 * Statutory Breakdown:
 * - Cancellation / No-Show: 0% SC (Exempt), 0% Municipality (Exempt), 5% VAT on base, 0 TD (Exempt).
 * - Extra Bed: 10% SC, 7% Municipality, 5% VAT on subtotal, 0 TD (Strictly Exempt).
 * - F&B / Spa / Manual Charges: 10% SC, 7% Municipality, 5% VAT on subtotal, 0 TD (Strictly Exempt).
 */
export const calculateDubaiItemizedChargeTax = (options = {}) => {
  const {
    baseAmount = 0,
    category = '',
    isDubai = true,
    customRates: customRatesProp = null,
    taxes = null,
    activeTaxes = null,
    taxSlabs = null
  } = options;

  const customRates = customRatesProp || (taxes || activeTaxes || taxSlabs ? extractUaeCustomRates(taxes || activeTaxes || taxSlabs) : null);

  const base = Math.max(0, Number(baseAmount) || 0);
  const cat = String(category || '').toLowerCase().trim();
  const isCancellation = cat.includes('cancellation') || cat.includes('no-show') || cat.includes('noshow');

  const serviceChargePct = isCancellation
    ? 0
    : (customRates?.serviceChargePct !== undefined ? Number(customRates.serviceChargePct) : DUBAI_DEFAULT_RATES.serviceChargePct);

  const municipalityFeePct = isCancellation || !isDubai
    ? 0
    : (customRates?.municipalityFeePct !== undefined ? Number(customRates.municipalityFeePct) : DUBAI_DEFAULT_RATES.municipalityFeePct);

  const vatPct = customRates?.vatPct !== undefined ? Number(customRates.vatPct) : DUBAI_DEFAULT_RATES.vatPct;

  const serviceChargeAmount = Number(((base * serviceChargePct) / 100).toFixed(2));
  const municipalityFeeAmount = Number(((base * municipalityFeePct) / 100).toFixed(2));
  const subtotalBeforeVat = Number((base + serviceChargeAmount + municipalityFeeAmount).toFixed(2));
  const vatAmount = Number(((subtotalBeforeVat * vatPct) / 100).toFixed(2));
  const tourismDirhamAmount = 0; // Strictly exempt on extra beds, F&B, Spa, and manual charges
  const totalTaxAmount = Number((serviceChargeAmount + municipalityFeeAmount + vatAmount).toFixed(2));
  const totalPostAmount = Number((base + totalTaxAmount).toFixed(2));

  return {
    base,
    serviceChargePct,
    serviceChargeAmount,
    municipalityFeePct,
    municipalityFeeAmount,
    subtotalBeforeVat,
    vatPct,
    vatAmount,
    tourismDirhamAmount,
    totalTaxAmount,
    totalPostAmount,
    isCancellation
  };
};

/**
 * Reverse-calculates base charge amount from a tax-inclusive total amount.
 * Formula: Base = Total / ((1 + (scPct + munPct)/100) * (1 + vatPct/100))
 */
export const reverseDubaiItemizedChargeTax = (options = {}) => {
  const {
    totalAmount = 0,
    category = '',
    isDubai = true,
    customRates: customRatesProp = null,
    taxes = null,
    activeTaxes = null,
    taxSlabs = null
  } = options;

  const customRates = customRatesProp || (taxes || activeTaxes || taxSlabs ? extractUaeCustomRates(taxes || activeTaxes || taxSlabs) : null);

  const total = Math.max(0, Number(totalAmount) || 0);
  const cat = String(category || '').toLowerCase().trim();
  const isCancellation = cat.includes('cancellation') || cat.includes('no-show') || cat.includes('noshow');

  const scPct = isCancellation
    ? 0
    : (customRates?.serviceChargePct !== undefined ? Number(customRates.serviceChargePct) : DUBAI_DEFAULT_RATES.serviceChargePct);
  const munPct = isCancellation || !isDubai
    ? 0
    : (customRates?.municipalityFeePct !== undefined ? Number(customRates.municipalityFeePct) : DUBAI_DEFAULT_RATES.municipalityFeePct);
  const vatPct = customRates?.vatPct !== undefined ? Number(customRates.vatPct) : DUBAI_DEFAULT_RATES.vatPct;

  const multiplier = (1 + (scPct + munPct) / 100) * (1 + vatPct / 100);
  const base = multiplier > 0 ? Number((total / multiplier).toFixed(2)) : total;

  return calculateDubaiItemizedChargeTax({
    baseAmount: base,
    category,
    isDubai,
    customRates
  });
};

/**
 * Computes consolidated UAE statutory taxes & hospitality fees breakdown
 * across room nights and incidental folio charges.
 */
export const calculateDubaiFolioBreakdown = (options = {}) => {
  const {
    folio,
    reservation,
    tenant,
    customRates: customRatesProp = null,
    taxes = null,
    activeTaxes = null,
    taxSlabs = null
  } = options;

  const customRates = customRatesProp || (taxes || activeTaxes || taxSlabs ? extractUaeCustomRates(taxes || activeTaxes || taxSlabs) : null);

  const nights = (reservation?.checkIn && reservation?.checkOut)
    ? Math.max(1, Math.round((new Date(reservation.checkOut) - new Date(reservation.checkIn)) / (1000 * 60 * 60 * 24)))
    : (Number(reservation?.nights) || 1);
  const bedroomsCount = (reservation?.rooms && reservation.rooms.length > 0) ? reservation.rooms.length : 1;
  const rating = tenant?.rating || reservation?.hotel?.rating || 4;
  const hotelType = tenant?.type || reservation?.hotel?.type || '';

  const rawItems = (folio?.items || folio?.transactions || []);

  const activeCharges = rawItems.filter(i =>
    !i.isVoided &&
    (i.type === 'Charge' || i.category === 'Charge' ||
     (!['Payment', 'Deposit', 'Refund', 'Settlement', 'Adjust'].includes(i.type) &&
      !['Payment', 'Deposit', 'Refund', 'Settlement'].includes(i.category) &&
      Number(i.amount || i.value || 0) > 0))
  );

  const roomItems = activeCharges.filter(i => {
    const desc = String(i.description || i.item || '').toLowerCase();
    const cat = String(i.category || '').toLowerCase();
    return cat === 'room' || cat === 'room charge' || desc.includes('nightly room charge') || desc.includes('room tariff');
  });

  const roomBaseFromItems = roomItems.reduce((s, i) => s + Number(i.amount || i.value || 0), 0);

  const roomTaxCalc = calculateDubaiHospitalityTax({
    netBaseAmount: roomBaseFromItems > 0 ? roomBaseFromItems : Number(reservation?.pricing?.roomTotal || reservation?.billing?.roomCharge || reservation?.totalAmount || 0),
    nights,
    bedroomsCount,
    rating,
    hotelType,
    customRates
  });

  let roomSc = reservation?.billing?.serviceCharge !== undefined ? Number(reservation.billing.serviceCharge) : roomTaxCalc.serviceCharge;
  let roomMun = reservation?.billing?.municipalityFee !== undefined ? Number(reservation.billing.municipalityFee) : roomTaxCalc.municipalityFee;
  let roomVat = reservation?.billing?.vat !== undefined ? Number(reservation.billing.vat) : roomTaxCalc.vat;
  let roomTd = reservation?.billing?.tourismDirham !== undefined ? Number(reservation.billing.tourismDirham) : roomTaxCalc.tourismDirham;

  const nonRoomItems = activeCharges.filter(i => !roomItems.includes(i));

  let itemizedSc = 0;
  let itemizedMun = 0;
  let itemizedVat = 0;

  nonRoomItems.forEach(item => {
    const totalAmount = Math.abs(Number(item.amount || item.value || 0));
    const desc = String(item.description || item.item || '').toLowerCase();
    const cat = String(item.category || '').toLowerCase();
    const isCancel = cat.includes('cancellation') || desc.includes('cancellation') || cat.includes('no-show') || desc.includes('no-show');

    let breakdown;
    if (item.baseAmount !== undefined && Number(item.baseAmount) > 0) {
      breakdown = calculateDubaiItemizedChargeTax({
        baseAmount: Number(item.baseAmount),
        category: isCancel ? 'cancellation' : (cat || desc),
        customRates
      });
    } else {
      breakdown = reverseDubaiItemizedChargeTax({
        totalAmount,
        category: isCancel ? 'cancellation' : (cat || desc),
        customRates
      });
    }

    itemizedSc += breakdown.serviceChargeAmount;
    itemizedMun += breakdown.municipalityFeeAmount;
    itemizedVat += breakdown.vatAmount;
  });

  const totalServiceCharge = Number((roomSc + itemizedSc).toFixed(2));
  const totalMunicipalityFee = Number((roomMun + itemizedMun).toFixed(2));
  const totalVat = Number((roomVat + itemizedVat).toFixed(2));
  const totalTourismDirham = Number(roomTd.toFixed(2));
  const tdRate = roomTaxCalc.tourismDirhamRate || 15;

  return {
    serviceCharge: totalServiceCharge,
    municipalityFee: totalMunicipalityFee,
    vat: totalVat,
    tourismDirham: totalTourismDirham,
    tourismDirhamRate: tdRate,
    serviceChargePct: roomTaxCalc.serviceChargePct,
    municipalityFeePct: roomTaxCalc.municipalityFeePct,
    vatPct: roomTaxCalc.vatPct,
    roomBreakdown: {
      serviceCharge: roomSc,
      municipalityFee: roomMun,
      vat: roomVat,
      tourismDirham: roomTd
    },
    itemizedBreakdown: {
      serviceCharge: itemizedSc,
      municipalityFee: itemizedMun,
      vat: itemizedVat
    }
  };
};

export default {
  DUBAI_DEFAULT_RATES,
  isUaeProperty,
  isDubaiProperty,
  extractUaeCustomRates,
  getTourismDirhamRate,
  calculateDubaiHospitalityTax,
  calculateDubaiItemizedChargeTax,
  reverseDubaiItemizedChargeTax,
  calculateDubaiFolioBreakdown
};
